"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useState } from "react";
import { motion } from "@/components/motion-wrapper";
import { cn } from "@/lib/utils";
import { randomFloatBetween } from "@llmgateway/shared/random";
import type { RefObject } from "react";
export interface AnimatedBeamProps {
  className?: string;
  containerRef: RefObject<HTMLElement | null>; // Container ref
  fromRef: RefObject<HTMLElement | null>;
  toRef: RefObject<HTMLElement | null>;
  curvature?: number;
  reverse?: boolean;
  pathColor?: string;
  pathWidth?: number;
  pathOpacity?: number;
  gradientStartColor?: string;
  gradientStopColor?: string;
  delay?: number;
  duration?: number;
  startXOffset?: number;
  startYOffset?: number;
  endXOffset?: number;
  endYOffset?: number;
}
export const AnimatedBeam: React.FC<AnimatedBeamProps> = t0 => {
  const $ = _c(58);
  const {
    className,
    containerRef,
    fromRef,
    toRef,
    curvature: t1,
    reverse: t2,
    duration: t3,
    delay: t4,
    pathColor: t5,
    pathWidth: t6,
    pathOpacity: t7,
    gradientStartColor: t8,
    gradientStopColor: t9,
    startXOffset: t10,
    startYOffset: t11,
    endXOffset: t12,
    endYOffset: t13
  } = t0;
  const curvature = t1 === undefined ? 0 : t1;
  const reverse = t2 === undefined ? false : t2;
  let t14;
  if ($[0] !== t3) {
    t14 = t3 === undefined ? randomFloatBetween(4, 7) : t3;
    $[0] = t3;
    $[1] = t14;
  } else {
    t14 = $[1];
  }
  const duration = t14;
  const delay = t4 === undefined ? 0 : t4;
  const pathColor = t5 === undefined ? "gray" : t5;
  const pathWidth = t6 === undefined ? 2 : t6;
  const pathOpacity = t7 === undefined ? 0.2 : t7;
  const gradientStartColor = t8 === undefined ? "#ffaa40" : t8;
  const gradientStopColor = t9 === undefined ? "#9c40ff" : t9;
  const startXOffset = t10 === undefined ? 0 : t10;
  const startYOffset = t11 === undefined ? 0 : t11;
  const endXOffset = t12 === undefined ? 0 : t12;
  const endYOffset = t13 === undefined ? 0 : t13;
  const id = useId();
  const [pathD, setPathD] = useState("");
  let t15;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      width: 0,
      height: 0
    };
    $[2] = t15;
  } else {
    t15 = $[2];
  }
  const [svgDimensions, setSvgDimensions] = useState(t15);
  let t16;
  if ($[3] !== reverse) {
    t16 = reverse ? {
      x1: ["90%", "-10%"],
      x2: ["100%", "0%"],
      y1: ["0%", "0%"],
      y2: ["0%", "0%"]
    } : {
      x1: ["10%", "110%"],
      x2: ["0%", "100%"],
      y1: ["0%", "0%"],
      y2: ["0%", "0%"]
    };
    $[3] = reverse;
    $[4] = t16;
  } else {
    t16 = $[4];
  }
  const gradientCoordinates = t16;
  let t17;
  let t18;
  if ($[5] !== containerRef || $[6] !== curvature || $[7] !== endXOffset || $[8] !== endYOffset || $[9] !== fromRef || $[10] !== startXOffset || $[11] !== startYOffset || $[12] !== toRef) {
    t17 = () => {
      const updatePath = () => {
        if (containerRef.current && fromRef.current && toRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const rectA = fromRef.current.getBoundingClientRect();
          const rectB = toRef.current.getBoundingClientRect();
          const svgWidth = containerRect.width;
          const svgHeight = containerRect.height;
          setSvgDimensions({
            width: svgWidth,
            height: svgHeight
          });
          const startX = rectA.left - containerRect.left + rectA.width / 2 + startXOffset;
          const startY = rectA.top - containerRect.top + rectA.height / 2 + startYOffset;
          const endX = rectB.left - containerRect.left + rectB.width / 2 + endXOffset;
          const endY = rectB.top - containerRect.top + rectB.height / 2 + endYOffset;
          const controlY = startY - curvature;
          const d = `M ${startX},${startY} Q ${(startX + endX) / 2},${controlY} ${endX},${endY}`;
          setPathD(d);
        }
      };
      const resizeObserver = new ResizeObserver(entries => {
        for (const _entry of entries) {
          updatePath();
        }
      });
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      updatePath();
      return () => {
        resizeObserver.disconnect();
      };
    };
    t18 = [containerRef, fromRef, toRef, curvature, startXOffset, startYOffset, endXOffset, endYOffset];
    $[5] = containerRef;
    $[6] = curvature;
    $[7] = endXOffset;
    $[8] = endYOffset;
    $[9] = fromRef;
    $[10] = startXOffset;
    $[11] = startYOffset;
    $[12] = toRef;
    $[13] = t17;
    $[14] = t18;
  } else {
    t17 = $[13];
    t18 = $[14];
  }
  useEffect(t17, t18);
  const t19 = svgDimensions.width;
  const t20 = svgDimensions.height;
  let t21;
  if ($[15] !== className) {
    t21 = cn("pointer-events-none absolute left-0 top-0 transform-gpu stroke-2", className);
    $[15] = className;
    $[16] = t21;
  } else {
    t21 = $[16];
  }
  const t22 = `0 0 ${svgDimensions.width} ${svgDimensions.height}`;
  let t23;
  if ($[17] !== pathColor || $[18] !== pathD || $[19] !== pathOpacity || $[20] !== pathWidth) {
    t23 = <path d={pathD} stroke={pathColor} strokeWidth={pathWidth} strokeOpacity={pathOpacity} strokeLinecap="round" />;
    $[17] = pathColor;
    $[18] = pathD;
    $[19] = pathOpacity;
    $[20] = pathWidth;
    $[21] = t23;
  } else {
    t23 = $[21];
  }
  const t24 = `url(#${id})`;
  let t25;
  if ($[22] !== pathD || $[23] !== pathWidth || $[24] !== t24) {
    t25 = <path d={pathD} strokeWidth={pathWidth} stroke={t24} strokeOpacity="1" strokeLinecap="round" />;
    $[22] = pathD;
    $[23] = pathWidth;
    $[24] = t24;
    $[25] = t25;
  } else {
    t25 = $[25];
  }
  let t26;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      x1: "0%",
      x2: "0%",
      y1: "0%",
      y2: "0%"
    };
    $[26] = t26;
  } else {
    t26 = $[26];
  }
  let t27;
  if ($[27] !== gradientCoordinates.x1 || $[28] !== gradientCoordinates.x2 || $[29] !== gradientCoordinates.y1 || $[30] !== gradientCoordinates.y2) {
    t27 = {
      x1: gradientCoordinates.x1,
      x2: gradientCoordinates.x2,
      y1: gradientCoordinates.y1,
      y2: gradientCoordinates.y2
    };
    $[27] = gradientCoordinates.x1;
    $[28] = gradientCoordinates.x2;
    $[29] = gradientCoordinates.y1;
    $[30] = gradientCoordinates.y2;
    $[31] = t27;
  } else {
    t27 = $[31];
  }
  let t28;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = [0.16, 1, 0.3, 1];
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  let t29;
  if ($[33] !== delay || $[34] !== duration) {
    t29 = {
      delay,
      duration,
      ease: t28,
      repeat: Infinity,
      repeatDelay: 0
    };
    $[33] = delay;
    $[34] = duration;
    $[35] = t29;
  } else {
    t29 = $[35];
  }
  let t30;
  let t31;
  if ($[36] !== gradientStartColor) {
    t30 = <stop stopColor={gradientStartColor} stopOpacity="0" />;
    t31 = <stop stopColor={gradientStartColor} />;
    $[36] = gradientStartColor;
    $[37] = t30;
    $[38] = t31;
  } else {
    t30 = $[37];
    t31 = $[38];
  }
  let t32;
  let t33;
  if ($[39] !== gradientStopColor) {
    t32 = <stop offset="32.5%" stopColor={gradientStopColor} />;
    t33 = <stop offset="100%" stopColor={gradientStopColor} stopOpacity="0" />;
    $[39] = gradientStopColor;
    $[40] = t32;
    $[41] = t33;
  } else {
    t32 = $[40];
    t33 = $[41];
  }
  let t34;
  if ($[42] !== id || $[43] !== t27 || $[44] !== t29 || $[45] !== t30 || $[46] !== t31 || $[47] !== t32 || $[48] !== t33) {
    t34 = <defs><motion.linearGradient className="transform-gpu" id={id} gradientUnits="userSpaceOnUse" initial={t26} animate={t27} transition={t29}>{t30}{t31}{t32}{t33}</motion.linearGradient></defs>;
    $[42] = id;
    $[43] = t27;
    $[44] = t29;
    $[45] = t30;
    $[46] = t31;
    $[47] = t32;
    $[48] = t33;
    $[49] = t34;
  } else {
    t34 = $[49];
  }
  let t35;
  if ($[50] !== svgDimensions.height || $[51] !== svgDimensions.width || $[52] !== t21 || $[53] !== t22 || $[54] !== t23 || $[55] !== t25 || $[56] !== t34) {
    t35 = <svg fill="none" width={t19} height={t20} xmlns="http://www.w3.org/2000/svg" className={t21} viewBox={t22}>{t23}{t25}{t34}</svg>;
    $[50] = svgDimensions.height;
    $[51] = svgDimensions.width;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
    $[55] = t25;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  return t35;
};